import { Capacitor } from '@capacitor/core'
import { Logger } from '@egw/logger'
import { configClient, isValidConfig } from '@egw/config'
import { type Config, configSchema } from '../../config/config'
import { AppType } from '../../models'

const log = new Logger('Platform Service')

declare global {
  interface Window {
    matchMedia: (query: string) => MediaQueryList
  }
}

export interface TAllPlatform {
  appName: string
  appVersion: string
  appType: string | null
  appPlatform: string
  isNativePlatform: boolean
  isNativeOrPwaPlatform: boolean
  isPwaPlatform: boolean
  isWebPlatform: boolean
}

export interface TPlatformService {
  getAppName: () => string
  getAppVersion: () => string
  getAppPlatform: () => string
  getAppType: () => string | null
  getIsNativePlatform: () => boolean
  getIsNativeOrPwaPlatform: () => boolean
  getIsPwaPlatform: () => boolean
  getIsWebPlatform: () => boolean
  getAllPlatform: () => TAllPlatform
}

const getAppName: () => string = () => {
  const config = configClient.getConfig() as Config
  if (!isValidConfig(config, configSchema)) {
    log.error('Platform configuration is invalid')
    throw new Error('Platform configuration is invalid')
  }
  return config.appName
}

const getAppVersion: () => string = () => {
  const config = configClient.getConfig() as Config
  if (!isValidConfig(config, configSchema)) {
    log.error('Platform configuration is invalid')
    throw new Error('Platform configuration is invalid')
  }
  return config.appVersion
}

const getIsNativePlatform: () => boolean = () => Capacitor.isNativePlatform()
const getAppPlatform: () => string = () => Capacitor.getPlatform()

const getAppType = (): string | null => {
  if (typeof window !== 'undefined') {
    let type = 'unknown'
    if (getAppPlatform() === 'web') {
      type = window.matchMedia('(display-mode: standalone)').matches ? AppType.Pwa : AppType.Browser
    } else if (getAppPlatform() === 'android' || getAppPlatform() === 'ios') {
      type = AppType.Capacitor
    }
    return type
  }
  return null
}

const getIsPwaPlatform = (): boolean => {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(display-mode: standalone)').matches || document.referrer.includes('android-app://')
}

const getIsNativeOrPwaPlatform: () => boolean = () => getIsNativePlatform() || getIsPwaPlatform()
const getIsWebPlatform: () => boolean = () => Capacitor.getPlatform() === 'web'

const getAllPlatform = (): TAllPlatform => ({
  appName: getAppName(),
  appVersion: getAppVersion(),
  appPlatform: getAppPlatform(),
  appType: getAppType(),
  isNativePlatform: getIsNativePlatform(),
  isNativeOrPwaPlatform: getIsNativeOrPwaPlatform(),
  isPwaPlatform: getIsPwaPlatform(),
  isWebPlatform: getIsWebPlatform(),
})

export const platformService: TPlatformService = {
  getAppName,
  getAppVersion,
  getAppPlatform: Capacitor.getPlatform,
  getAppType,
  getIsNativePlatform: Capacitor.isNativePlatform,
  getIsNativeOrPwaPlatform,
  getIsPwaPlatform,
  getIsWebPlatform,
  getAllPlatform,
}
